import {
  compact,
  differenceBy,
  filter,
  find,
  get,
  map,
  mergeWith,
} from 'lodash';
import {
  rx,
  BehaviorSubject,
  Observable,
  observableCombineLatest,
} from '@shared/rx';

import { XVIError } from '@shared/models';
import { asObservable } from '@shared/utils';
import { mergeCustomizer } from './datasource-utils';

/**
 * SelectOptionsDataSource is a special datasource class that
 * uses SelectOptions or Observable of SelectOptions instead of API service.
 * Can be used to feed already acquired data to entity widgets.
 */
export class SelectOptionsDataSource {
  public filterText: BehaviorSubject<string> = new BehaviorSubject('');
  public selection: BehaviorSubject<any[]> = new BehaviorSubject([]);

  public busy: BehaviorSubject<boolean> = new BehaviorSubject(false);
  public error: BehaviorSubject<XVIError> = new BehaviorSubject(null);
  public exhausted: BehaviorSubject<boolean> = new BehaviorSubject(false);
  public items: BehaviorSubject<any[]> = new BehaviorSubject([]);
  public selectedItems: BehaviorSubject<any[]> = new BehaviorSubject([]);
  public availableItems: BehaviorSubject<any[]> = new BehaviorSubject([]);
  public visibleSelectedItemsObs: Observable<any[]> =
    this.selectedItems.asObservable();

  public constructor(selectOptions, options) {
    mergeWith(this, options, mergeCustomizer);
    observableCombineLatest([
      asObservable(selectOptions),
      this.filterText.asObservable(),
    ])
      .pipe(
        rx.map(([items, filterText]) => {
          const regexp: RegExp = RegExp(filterText, 'i');

          return filter(items, ({ text }) => regexp.test(text));
        }),
      )
      .subscribe(this.items);

    observableCombineLatest([
      asObservable(selectOptions),
      this.selection.asObservable(),
    ])
      .pipe(
        rx.map(([items, selection]) =>
          compact(map(selection, (value) => find(items, { value }))),
        ),
      )
      .subscribe(this.selectedItems);

    observableCombineLatest([
      this.items.asObservable(),
      this.selectedItems.asObservable(),
    ])
      .pipe(
        rx.map(([items, selected]) =>
          differenceBy(items, selected, this.getItemValue),
        ),
      )
      .subscribe(this.availableItems);
  }

  public setFilter(filterText: string): void {
    this.filterText.next(filterText);
  }

  public getItemValue(item): any {
    return get(item, 'value');
  }

  public getItemText(item): string {
    return get(item, 'text');
  }

  public nextPage(): void {
    this.exhausted.next(true);
  }

  public composeFilterModel(filterText: string): object {
    this.filterText.next(filterText);

    return {
      text: {
        filterText,
      },
    };
  }

  public getRows(options): void {
    options.successCallback(this.items.value, this.items.value.length);
    this.busy.next(false);
  }

  public setSelection(selection): void {
    this.selection.next(selection);
  }
}
